import type { ReactNode } from 'react';

interface FieldProps {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
}

/**
 * Label + control + error, wired for screen readers and for the form's
 * "scroll to the first invalid field" behaviour (the wrapper carries the id).
 */
export function Field({ id, label, error, hint, required, children }: FieldProps) {
  return (
    <div data-field={id} className="scroll-mt-24">
      <label htmlFor={id} className="field-label">
        {label}
        {required ? <span className="ml-1 text-red-600">*</span> : null}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      {hint && !error ? (
        <p id={`${id}-hint`} className="field-hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Same wrapper for groups of radio cards, which have no single labelable input. */
export function FieldGroup({
  id,
  label,
  error,
  hint,
  required,
  children,
}: Omit<FieldProps, 'id'> & { id: string }) {
  return (
    <fieldset data-field={id} className="scroll-mt-24 border-0 p-0">
      <legend className="field-label">
        {label}
        {required ? <span className="ml-1 text-red-600">*</span> : null}
      </legend>
      {children}
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
      {hint && !error ? <p className="field-hint">{hint}</p> : null}
    </fieldset>
  );
}
